/**
 * Shared constants for Monday.com column types used across form editors.
 *
 * When Monday adds new column types, update these arrays in one place
 * and every form editor picks them up automatically.
 */

// ── Error codes ──────────────────────────────────────────────────────────────

/**
 * Error codes returned by the subitem-form creation endpoint.
 * Keep in sync with the backend's FORM_ERROR (common.constant.ts).
 */
export const SUBITEM_FORM_ERROR = {
  NO_SUBITEM_BOARD: 'no_subitem_board',
  LOOKUP_FAILED: 'subitem_board_lookup_failed',
};

// ── Column type filters ──────────────────────────────────────────────────────

/**
 * All Monday column types accepted as form fields.
 * Used when fetching board columns to filter out unsupported types.
 */
export const ACCEPTED_COLUMN_TYPES = [
  'name', 'text', 'color', 'dropdown', 'file', 'boolean', 'long-text', 'date',
  'rating', 'numeric', 'numbers', 'long_text', 'phone', 'location', 'mirror',
  'status', 'link', 'email', 'timeline', 'formula', 'people', 'person', 'board_relation',
];

/**
 * Inherently computed column types. Monday derives their values from other data,
 * so they can never be written through the API and a respondent can never
 * satisfy them. Always render them read-only, never offer Required/Editable for
 * them, and exempt them from required validation.
 *
 * Keep in sync with the backend's READ_ONLY_COLUMN_TYPES (constants/column.constant.ts).
 */
export const READ_ONLY_COLUMN_TYPES = ['mirror', 'formula', 'board_relation'];

/**
 * True when a column's value is computed by Monday and therefore read-only.
 */
export const isReadOnlyColumnType = (type) => READ_ONLY_COLUMN_TYPES.includes(type);

/**
 * Column types that support conditional logic (if/else rules).
 */
export const CONDITIONAL_LOGIC_TYPES = [
  'status', 'color', 'dropdown', 'text', 'long-text', 'boolean', 'numbers', 'numeric', 'date',
];

/**
 * Column types that render as choice/select inputs (status labels or dropdown options).
 */
export const CHOICE_COLUMN_TYPES = ['status', 'color', 'dropdown'];

/**
 * Form-sourced field types a respondent can fill directly ON the PDF
 * (Fill-on-PDF view).
 *
 * The admission rule is display parity: what the respondent sees in the box
 * must be exactly the string the stamper will draw. Free-text types satisfy it
 * trivially. The closed-set types below satisfy it because their control is
 * built to render the stamped form of the value rather than a friendlier one —
 * a rating picker offers "4 / 5", not "4"; a checkbox shows "Yes"/"No", not a
 * tick; a dropdown shows its labels comma-joined. See `stampedText()` in
 * utils/pdfOverlays.js, which mirrors the server's column-display module.
 *
 * `date` and `timeline` use native date inputs. Their on-screen rendering is
 * the viewer's locale while the stamp is ISO — a formatting difference, not a
 * semantic one, because a date chosen from a calendar is the same day either
 * way. That is a weaker bar than the closed sets clear, and it is the reason
 * these are listed separately here.
 *
 * Everything NOT here is unfillable for a structural reason rather than a
 * missing widget: `file` in upload mode (a PDF cannot contain the file, and an
 * upload widget cannot live on a fixed page), and mirror / formula /
 * board_relation, which monday computes and never accepts a write for — a
 * respondent could not satisfy them in any view.
 *
 * Signature fields are handled separately by `isSignatureField` below — a pad
 * can be drawn on a fixed page. A file field in upload mode cannot: the widget
 * has nowhere to live and the PDF could not contain the file anyway.
 */
export const FILL_ON_PDF_FIELD_TYPES = [
  'text', 'name', 'email', 'link', 'phone', 'location', 'long-text', 'long_text',
  'numeric', 'numbers',
  // Closed sets — a picker can offer exactly the stamped strings.
  'status', 'color', 'dropdown', 'rating', 'boolean',
  // Native pickers; locale display vs ISO stamp is cosmetic (see above).
  'date', 'timeline',
  // Board members, chosen from the same subscriber list the standard form uses.
  'people', 'person',
];

/**
 * Is this field collected as a drawn signature?
 *
 * Two shapes mean the same thing: an item column is `file` with
 * `signature: true` (monday has no signature column type), while the subitem
 * vocabulary names it directly as type `signature`.
 *
 * `upload` is deliberately not consulted. The two flags are independent
 * checkboxes and both can be set; when they are, `SubmissionForm` renders the
 * signature pad regardless of `upload`, so a signature is what comes back.
 *
 * Mirrored by `isSignatureField` in apr-backend/src/utils/signature-field.ts.
 */
export const isSignatureField = (field) => {
  if (!field) return false;
  if (field.type === 'signature') return true;
  return field.type === 'file' && field.signature === true;
};

/** True when a form field can take direct input in the Fill-on-PDF view. */
export const isFillOnPdfFillable = (field) => {
  if (!field) return false;
  // Signature-first, and it must be checked BEFORE the type list: a pad can be
  // drawn on a fixed page, so a signature field qualifies however its `upload`
  // flag is set. Requiring `upload !== true` here excluded fields that the
  // standard form was happily collecting signatures for — the respondent could
  // sign on the standard form and had no control at all on the PDF.
  if (isSignatureField(field)) return true;
  // A file field that is NOT a signature stays out: an upload widget cannot
  // live on a fixed page and a PDF cannot contain the file anyway.
  if (field.type === 'file') return false;
  return FILL_ON_PDF_FIELD_TYPES.includes(field.type);
};

/**
 * Why a field cannot be filled on a PDF, phrased for the owner. Null when it can.
 *
 * The distinction worth drawing is FIXABLE vs structural. A File column with
 * `signature` off is one checkbox away from working, so "it would stamp blank"
 * is true and useless there — it reads as a limitation of the product rather
 * than a setting the owner controls, and leaves them with nowhere to go.
 * Everything else here genuinely cannot work on a fixed page.
 */
export const fillOnPdfBlockReason = (field) => {
  if (!field || isFillOnPdfFillable(field)) return null;
  if (field.type === 'file') {
    return field.upload
      ? 'File uploads can’t be collected on a PDF. Turn on “Signature capture” for this field on the Edit tab to let respondents sign here instead.'
      : 'Turn on “Signature capture” for this field on the Edit tab, then it can be signed here.';
  }
  if (isReadOnlyColumnType(field.type)) {
    return 'monday calculates this column, so a respondent can never fill it — place it from Item columns instead.';
  }
  return 'Respondents can’t fill this on a PDF — it would stamp blank.';
};

// ── Friendly type labels ─────────────────────────────────────────────────────

/**
 * Human-readable labels for Monday column types.
 * Used in mapping UI tooltips and placeholders.
 */
export const COLUMN_TYPE_LABELS = {
  email: 'Email',
  text: 'Text',
  numeric: 'Number',
  numbers: 'Number',
  rating: 'Rating',
  'long-text': 'Long Text',
  long_text: 'Long Text',
  color: 'Status',
  status: 'Status',
  link: 'Link',
};
